import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { publicApi } from '../services/api'
import { formatCurrency } from '../config'
import type { RegistrationFormData, Golfer, Contact } from '../types'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { formatPhoneNumber, lowercaseEmail, isValidEmail, isValidPhone } from '../utils/formatters'

const GOLF_PRICE_CENTS = 15000

const focusField = (id: string) => {
  const el = document.getElementById(id)
  if (el) {
    el.focus()
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}
// Banquet functionality hidden - see docs/BANQUET_RESTORATION.md to restore
// const BANQUET_PRICE_CENTS = 5000

const emptyGolfer = (): Golfer => ({ name: '', email: '', phone: '' })

interface WaitlistRegistrationData {
  registrationId: string
  eventId: string
  status: string
  primaryContact: Contact
  golfersCount: number
  banquetTicketsCount: number
  golfers: Golfer[]
  teamName?: string
  waitlistPosition: number
  createdAt: string
}

interface WaitlistEventData {
  eventId: string
  name: string
  slug: string
  date: string
  venue: string
}

export default function WaitlistPayment() {
  const { token } = useParams<{ token: string }>()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Original registration data (for reference)
  const [originalRegistration, setOriginalRegistration] = useState<WaitlistRegistrationData | null>(null)
  const [eventData, setEventData] = useState<WaitlistEventData | null>(null)

  // Form state (editable)
  const [primaryContact, setPrimaryContact] = useState<Contact>({ name: '', email: '', phone: '' })
  const [golfersCount, setGolfersCount] = useState(0)
  // Banquet hidden - keep for API compatibility but always 0
  const banquetCount = 0
  const [golfers, setGolfers] = useState<Golfer[]>([])
  const [teamName, setTeamName] = useState('')
  const [paidGolferIndices, setPaidGolferIndices] = useState<Set<number>>(new Set())

  // Load waitlist registration data
  useEffect(() => {
    async function fetchData() {
      if (!token) {
        setError('No token provided')
        setLoading(false)
        return
      }

      try {
        const response = await publicApi.getWaitlistRegistration(token)
        const reg = response.registration

        setOriginalRegistration(reg)
        setEventData(response.event)

        // Pre-populate form with existing data
        setPrimaryContact(reg.primaryContact)
        setGolfersCount(reg.golfersCount)
        // banquetCount is now always 0, ignore stored value
        setGolfers(reg.golfers || [])
        setTeamName(reg.teamName || '')
        // Default all golfers as paid
        const indices = new Set<number>()
        for (let i = 0; i < reg.golfersCount; i++) indices.add(i)
        setPaidGolferIndices(indices)

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load registration')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [token])

  // Update golfers array when count changes
  useEffect(() => {
    const newGolfers = [...golfers]
    while (newGolfers.length < golfersCount) {
      newGolfers.push(emptyGolfer())
    }
    while (newGolfers.length > golfersCount) {
      newGolfers.pop()
    }
    setGolfers(newGolfers)
    // Manage paid golfer indices
    setPaidGolferIndices(prev => {
      const updated = new Set(prev)
      for (let i = 0; i < golfersCount; i++) {
        if (!prev.has(i) && i >= golfers.length) updated.add(i)
      }
      for (const idx of updated) {
        if (idx >= golfersCount) updated.delete(idx)
      }
      if (updated.size === 0 && golfersCount > 0) {
        for (let i = 0; i < golfersCount; i++) updated.add(i)
      }
      return updated
    })
  }, [golfersCount])

  const updateGolfer = (index: number, field: keyof Golfer, value: string) => {
    const newGolfers = [...golfers]
    newGolfers[index] = { ...newGolfers[index], [field]: value }
    setGolfers(newGolfers)
  }

  const toggleGolferPaid = (index: number) => {
    setPaidGolferIndices(prev => {
      const updated = new Set(prev)
      if (updated.has(index)) updated.delete(index)
      else updated.add(index)
      return updated
    })
  }

  const copyPrimaryToFirstGolfer = () => {
    if (golfers.length > 0) {
      const newGolfers = [...golfers]
      newGolfers[0] = { ...primaryContact }
      setGolfers(newGolfers)
    }
  }

  const paidGolfersCount = paidGolferIndices.size
  const totalAmount = paidGolfersCount * GOLF_PRICE_CENTS

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!token || !eventData) {
      setError('Invalid registration state')
      return
    }

    if (golfersCount === 0) {
      setError('Please select at least one golfer')
      return
    }

    if (paidGolfersCount === 0) {
      setError('At least one golfer must be marked as paid to proceed to payment')
      return
    }

    // Validate primary contact fields
    if (!primaryContact.name.trim()) {
      setError('Please enter the primary contact name')
      focusField('primary-name')
      return
    }
    if (!primaryContact.email.trim()) {
      setError('Please enter the primary contact email')
      focusField('primary-email')
      return
    }
    if (!isValidEmail(primaryContact.email)) {
      setError('Please enter a valid email address for the primary contact')
      focusField('primary-email')
      return
    }
    if (!primaryContact.phone.trim()) {
      setError('Please enter the primary contact phone number')
      focusField('primary-phone')
      return
    }
    if (!isValidPhone(primaryContact.phone)) {
      setError('Please enter a valid 10-digit phone number for the primary contact')
      focusField('primary-phone')
      return
    }

    // Validate golfer fields
    for (let i = 0; i < golfersCount; i++) {
      if (!golfers[i].name.trim()) {
        setError(`Please enter a name for Golfer ${i + 1}`)
        focusField(`golfer-${i}-name`)
        return
      }
      if (!golfers[i].email.trim()) {
        setError(`Please enter an email for Golfer ${i + 1}`)
        focusField(`golfer-${i}-email`)
        return
      }
      if (!isValidEmail(golfers[i].email)) {
        setError(`Golfer ${i + 1} has an invalid email address`)
        focusField(`golfer-${i}-email`)
        return
      }
      if (!golfers[i].phone.trim()) {
        setError(`Please enter a phone number for Golfer ${i + 1}`)
        focusField(`golfer-${i}-phone`)
        return
      }
      if (!isValidPhone(golfers[i].phone)) {
        setError(`Golfer ${i + 1} has an invalid phone number (10 digits required)`)
        focusField(`golfer-${i}-phone`)
        return
      }
    }

    setSubmitting(true)

    try {
      const formData: RegistrationFormData & { waitlistToken: string } = {
        waitlistToken: token,
        eventSlug: eventData.slug,
        primaryContact,
        golfersCount,
        banquetTicketsCount: banquetCount,
        golfers: golfers.slice(0, golfersCount).map((g, i) => ({
          ...g,
          paying: paidGolferIndices.has(i),
        })),
        teamName: teamName || undefined,
        paidGolferIndices: Array.from(paidGolferIndices),
      }

      const response = await publicApi.createWaitlistCheckout(formData)
      // Redirect to Stripe Checkout
      window.location.href = response.checkoutUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create checkout session')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error && !originalRegistration) {
    return (
      <div className="card text-center py-12">
        <div className="mb-4">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Unable to Load Registration</h1>
        <p className="text-red-600 mb-6">{error}</p>
        <p className="text-gray-600 mb-6">
          The registration link may be invalid, expired, or already used.
          Please contact us if you believe this is an error.
        </p>
        <a href="/" className="btn btn-primary">
          Back to Home
        </a>
      </div>
    )
  }

  if (!originalRegistration || !eventData) {
    return null
  }

  return (
    <div>
      {/* Welcome Back Banner */}
      <div className="card bg-green-50 border-2 border-green-300 mb-8">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Great News! Complete Your Registration</h2>
            <p className="text-gray-700 mb-2">
              A spot has opened up for you! Your waitlist registration information is pre-filled below.
            </p>
            <p className="text-gray-600 text-sm">
              You can review and update your information if needed, then proceed to payment to secure your spot.
            </p>
          </div>
        </div>
      </div>

      {/* Event Hero Section */}
      <div className="card mb-8 overflow-hidden">
        <img
          src="/images/angels-on-the-green.png"
          alt="Angels on the Green Golf Tournament"
          className="w-full h-48 md:h-64 object-cover -mx-6 -mt-6 mb-6"
          style={{ width: 'calc(100% + 3rem)' }}
        />
        <div className="flex flex-col md:flex-row items-center gap-6">
          <img
            src="/images/logo.png"
            alt="School Lunch Angels"
            className="h-32 md:h-40 w-auto flex-shrink-0"
          />
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-bold text-sla-green mb-2">Angels on the Green</h1>
            <p className="text-xl text-gray-700 mb-4">Best Ball Scramble Golf Tournament</p>
            <div className="text-gray-600 space-y-1">
              <p className="font-medium">April 11, 2026</p>
              <p>11:00 AM Check In | 12:00 PM Shotgun Start | Awards Ceremony to Follow</p>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="font-semibold text-gray-800">Callaway Resort & Gardens</p>
              <p className="text-gray-600">Lake View Golf Course</p>
              <p className="text-gray-600">3396 Golf House Drive, Pine Mountain, GA 31822</p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Primary Contact */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Primary Contact Information
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            This person will receive confirmation emails and be the main point
            of contact.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name *</label>
              <input
                id="primary-name"
                type="text"
                className="input"
                value={primaryContact.name}
                onChange={(e) =>
                  setPrimaryContact({ ...primaryContact, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <label className="label">Email *</label>
              <input
                id="primary-email"
                type="email"
                className="input"
                value={primaryContact.email}
                onChange={(e) =>
                  setPrimaryContact({ ...primaryContact, email: e.target.value })
                }
                onBlur={(e) =>
                  setPrimaryContact({ ...primaryContact, email: lowercaseEmail(e.target.value) })
                }
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="label">Phone *</label>
              <input
                id="primary-phone"
                type="tel"
                className="input md:w-1/2"
                value={primaryContact.phone}
                onChange={(e) =>
                  setPrimaryContact({ ...primaryContact, phone: formatPhoneNumber(e.target.value) })
                }
                required
              />
            </div>
          </div>
        </div>

        {/* Registration Options */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Registration Options
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            You can update your selections if needed.
          </p>

          <div className="space-y-6">
            {/* Golf Registration */}
            <div className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <h3 className="font-semibold text-gray-800">
                    Golf Registration
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Includes golf, cart, and lunch
                  </p>
                </div>
                <span className="text-lg font-bold text-sla-blue">
                  {formatCurrency(GOLF_PRICE_CENTS)} each
                </span>
              </div>

              <div className="flex items-center gap-4">
                <label className="label mb-0">Number of Golfers:</label>
                <select
                  className="input w-24"
                  value={golfersCount}
                  onChange={(e) => setGolfersCount(Number(e.target.value))}
                >
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Banquet Only - HIDDEN - see docs/BANQUET_RESTORATION.md */}
          </div>
        </div>

        {/* Golfer Details */}
        {golfersCount > 0 && (
          <div className="card">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Golfer Information
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Please provide details for each golfer.
            </p>

            {golfers.map((golfer, index) => {
              const isPaid = paidGolferIndices.has(index)
              return (
                <div
                  key={index}
                  className={`border rounded-lg p-4 mb-4 last:mb-0 ${!isPaid ? 'bg-gray-50 border-dashed' : ''}`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-700">
                      Golfer {index + 1}
                    </h3>
                    {index === 0 && (
                      <button
                        type="button"
                        onClick={copyPrimaryToFirstGolfer}
                        className="text-sm text-sla-blue hover:underline"
                      >
                        Copy from primary contact
                      </button>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPaid}
                        onChange={() => toggleGolferPaid(index)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        I am paying for this golfer
                      </span>
                    </label>
                    {!isPaid && (
                      <p className="text-xs text-orange-600 mt-1 ml-6">
                        This golfer will receive an email to register and pay for themselves.
                      </p>
                    )}
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Full Name *</label>
                      <input
                        id={`golfer-${index}-name`}
                        type="text"
                        className="input"
                        value={golfer.name}
                        onChange={(e) =>
                          updateGolfer(index, 'name', e.target.value)
                        }
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Email *</label>
                      <input
                        id={`golfer-${index}-email`}
                        type="email"
                        className="input"
                        value={golfer.email}
                        onChange={(e) =>
                          updateGolfer(index, 'email', e.target.value)
                        }
                        onBlur={(e) =>
                          updateGolfer(index, 'email', lowercaseEmail(e.target.value))
                        }
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Phone *</label>
                      <input
                        id={`golfer-${index}-phone`}
                        type="tel"
                        className="input"
                        value={golfer.phone}
                        onChange={(e) =>
                          updateGolfer(index, 'phone', formatPhoneNumber(e.target.value))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Team Preferences */}
        {golfersCount > 0 && (
          <div className="card">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Team Preferences (Optional)
            </h2>

            <div className="space-y-4">
              <div>
                <label className="label">Team Name</label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g., The Birdies"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                />
              </div>

            </div>
          </div>
        )}

        {/* Order Summary */}
        <div className="card bg-gray-50">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Order Summary
          </h2>

          <div className="space-y-2 mb-4">
            {golfersCount > 0 && (
              <>
                <div className="flex justify-between">
                  <span>Golf Registration x {paidGolfersCount}{paidGolfersCount < golfersCount ? ` (paying for ${paidGolfersCount} of ${golfersCount} golfers)` : ''}</span>
                  <span>{formatCurrency(paidGolfersCount * GOLF_PRICE_CENTS)}</span>
                </div>
                {paidGolfersCount < golfersCount && (
                  <p className="text-xs text-orange-600">
                    {golfersCount - paidGolfersCount} golfer{golfersCount - paidGolfersCount > 1 ? 's' : ''} will receive an email to register and pay individually
                  </p>
                )}
              </>
            )}
          </div>

          <div className="border-t pt-4">
            <div className="flex justify-between text-xl font-bold">
              <span>Total</span>
              <span className="text-sla-blue">{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Refund Policy Notice */}
        {golfersCount > 0 && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-sm text-yellow-800">
            <strong>Refund Policy:</strong> Registrations are non-refundable after March 1st but are transferable. A $5 fee per golfer registration applies to any refund processed before that date.
          </div>
        )}

        {/* Rain-or-Shine Notice */}
        <p className="text-xs text-gray-500 text-center">
          This is a rain-or-shine event. In case of extreme circumstances (natural disasters, government orders, etc.), the backup date is Saturday, May 9, 2026.
        </p>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={submitting || golfersCount === 0}
            className="btn btn-primary text-lg px-8 py-3"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Processing...
              </span>
            ) : (
              `Proceed to Payment - ${formatCurrency(totalAmount)}`
            )}
          </button>
          <p className="text-gray-500 text-sm mt-2">
            You'll be redirected to Stripe for secure payment
          </p>
        </div>
      </form>
    </div>
  )
}
